/**
 * Store de Historial - Zustand + Dexie
 *
 * Maneja la persistencia del historial de protocolos:
 * - Guarda cada completado en IndexedDB
 * - Mantiene snapshots diarios
 * - Calcula estadísticas
 * - Prepara sync con Supabase
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  CompletionRecord,
  DaySnapshot,
  PeriodStats,
  HistoryFilters,
  ProtocolConsistency,
  Protocol,
  Layer,
  Frequency,
} from '@/core/types';
import {
  db,
  getTodayDate,
  generateId,
  createEmptyDaySnapshot,
} from '@/services/storage';

interface HistoryState {
  // === State ===
  isLoading: boolean;
  error: string | null;
  todaySnapshot: DaySnapshot | null;
  weekStats: PeriodStats | null;

  // === Actions: Registrar ===
  recordCompletion: (
    protocol: Protocol,
    completedSubtaskIds: string[],
    mode: 'normal' | 'hardDay' | 'weekend'
  ) => Promise<void>;

  recordUncompletion: (protocolId: string) => Promise<void>;

  // === Actions: Snapshots ===
  updateTodaySnapshot: (protocols: Protocol[], hardDayMode: boolean, weekendMode: boolean) => Promise<void>;
  loadTodaySnapshot: () => Promise<DaySnapshot | null>;

  // === Actions: Estadísticas ===
  calculateWeekStats: () => Promise<PeriodStats | null>;
  calculatePeriodStats: (period: 'week' | 'month' | 'quarter' | 'year' | 'all') => Promise<PeriodStats | null>;
  getProtocolConsistency: (protocolId: string) => Promise<ProtocolConsistency | null>;

  // === Actions: Queries ===
  getCompletions: (filters: HistoryFilters) => Promise<CompletionRecord[]>;
  getDaySnapshots: (startDate: string, endDate: string) => Promise<DaySnapshot[]>;
  getStreak: () => Promise<number>;

  // === Actions: Lifecycle ===
  initialize: () => Promise<void>;
}

export const useHistoryStore = create<HistoryState>()(
  immer((set, get) => ({
    // === Initial State ===
    isLoading: false,
    error: null,
    todaySnapshot: null,
    weekStats: null,

    // === Actions: Registrar ===
    recordCompletion: async (protocol, completedSubtaskIds, mode) => {
      try {
        const now = new Date();
        const date = getTodayDate();
        const timeBlock = getTimeBlockFromHour(now.getHours());

        const record: CompletionRecord = {
          id: generateId(),
          protocolId: protocol.id,
          protocolTitle: protocol.title,
          frequency: protocol.frequency,
          layer: protocol.layer,
          completedAt: now.toISOString(),
          date,
          timeEstimate: protocol.timeEstimate,
          completedSubtasks: completedSubtaskIds,
          totalSubtasks: protocol.subtasks.length,
          isPartial: completedSubtaskIds.length < protocol.subtasks.length,
          timeBlock,
          mode,
          syncStatus: 'pending',
          lastModified: now.toISOString(),
        };

        await db.completions.add(record);

        // Actualizar snapshot del día
        // Nota: updateTodaySnapshot maneja protocols vacios obteniendo completions de la BD
        // Los modos se actualizan segun el mode recibido
        const hardDayMode = mode === 'hardDay';
        const weekendMode = mode === 'weekend';
        await get().updateTodaySnapshot([], hardDayMode, weekendMode);
      } catch (error) {
        console.error('Error recording completion:', error);
        set({ error: 'Error al guardar completado' });
      }
    },

    recordUncompletion: async (protocolId) => {
      try {
        const date = getTodayDate();

        // Eliminar el último registro de hoy para este protocolo
        const records = await db.completions
          .where('[date+protocolId]')
          .equals([date, protocolId])
          .toArray();

        if (records.length > 0) {
          // Eliminar el más reciente
          const sortedRecords = records.sort(
            (a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime()
          );
          const lastRecord = sortedRecords[0];
          if (lastRecord) {
            await db.completions.delete(lastRecord.id);
          }
        }
      } catch (error) {
        console.error('Error removing completion:', error);
      }
    },

    // === Actions: Snapshots ===
    updateTodaySnapshot: async (protocols, hardDayMode, weekendMode) => {
      try {
        const date = getTodayDate();
        let snapshot = await db.daySnapshots.get(date);

        if (!snapshot) {
          snapshot = createEmptyDaySnapshot(date);
        }

        // Obtener todos los completados de hoy
        const todayCompletions = await db.completions
          .where('date')
          .equals(date)
          .toArray();

        // Calcular métricas por capa
        const completedByLayer = todayCompletions.reduce(
          (acc, c) => {
            if (!acc[c.layer].includes(c.protocolId)) {
              acc[c.layer].push(c.protocolId);
            }
            return acc;
          },
          { survival: [], maintenance: [], growth: [] } as Record<Layer, string[]>
        );

        // Si tenemos protocolos, calcular totales
        if (protocols.length > 0) {
          const dailyProtocols = protocols.filter(
            (p) => p.frequency === 'daily' && p.isActive
          );

          const byLayer = dailyProtocols.reduce(
            (acc, p) => {
              acc[p.layer].total++;
              acc[p.layer].timeEstimate += p.timeEstimate;
              return acc;
            },
            {
              survival: { total: 0, timeEstimate: 0 },
              maintenance: { total: 0, timeEstimate: 0 },
              growth: { total: 0, timeEstimate: 0 },
            } as Record<Layer, { total: number; timeEstimate: number }>
          );

          // Actualizar snapshot con datos de protocolos
          (['survival', 'maintenance', 'growth'] as Layer[]).forEach((layer) => {
            snapshot![layer] = {
              total: byLayer[layer].total,
              completed: completedByLayer[layer].length,
              percentage:
                byLayer[layer].total > 0
                  ? Math.round((completedByLayer[layer].length / byLayer[layer].total) * 100)
                  : 0,
              completedIds: completedByLayer[layer],
            };
          });

          snapshot.totalProtocols = dailyProtocols.length;
          snapshot.totalTimeEstimate = dailyProtocols.reduce((acc, p) => acc + p.timeEstimate, 0);
        }

        // Actualizar completados
        const completedIds = new Set(todayCompletions.map((c) => c.protocolId));
        snapshot.completedProtocols = completedIds.size;
        snapshot.completedTimeEstimate = todayCompletions.reduce(
          (acc, c) => acc + c.timeEstimate,
          0
        );
        snapshot.completionRate =
          snapshot.totalProtocols > 0
            ? Math.round((snapshot.completedProtocols / snapshot.totalProtocols) * 100)
            : 0;

        // Actualizar modos
        snapshot.usedHardDayMode = snapshot.usedHardDayMode || hardDayMode;
        snapshot.usedWeekendMode = snapshot.usedWeekendMode || weekendMode;

        // Guardar
        snapshot.lastModified = new Date().toISOString();
        await db.daySnapshots.put(snapshot);

        set({ todaySnapshot: snapshot });
      } catch (error) {
        console.error('Error updating snapshot:', error);
      }
    },

    loadTodaySnapshot: async () => {
      try {
        const date = getTodayDate();
        const snapshot = await db.daySnapshots.get(date);
        set({ todaySnapshot: snapshot || null });
        return snapshot || null;
      } catch (error) {
        console.error('Error loading snapshot:', error);
        return null;
      }
    },

    // === Actions: Estadísticas ===
    calculateWeekStats: async () => {
      const stats = await get().calculatePeriodStats('week');
      set({ weekStats: stats });
      return stats;
    },

    calculatePeriodStats: async (period) => {
      try {
        set({ isLoading: true });

        const { startDate, endDate } = getPeriodDates(period);

        const snapshots = await db.daySnapshots
          .where('date')
          .between(startDate, endDate, true, true)
          .toArray();

        if (snapshots.length === 0) {
          set({ isLoading: false });
          return null;
        }

        // Calcular estadísticas
        const totalDays = getDaysBetween(startDate, endDate);
        const activeDays = snapshots.length;

        const avgCompletionRate =
          snapshots.reduce((acc, s) => acc + s.completionRate, 0) / snapshots.length;

        const avgByLayer = {
          survival: snapshots.reduce((acc, s) => acc + s.survival.percentage, 0) / snapshots.length,
          maintenance:
            snapshots.reduce((acc, s) => acc + s.maintenance.percentage, 0) / snapshots.length,
          growth: snapshots.reduce((acc, s) => acc + s.growth.percentage, 0) / snapshots.length,
        };

        // Calcular rachas
        const { currentStreak, bestStreak } = calculateStreaks(snapshots);

        // Calcular patrones por día de la semana
        const byDayOfWeek: Record<number, { total: number; sum: number }> = {};
        snapshots.forEach((s) => {
          if (!byDayOfWeek[s.dayOfWeek]) {
            byDayOfWeek[s.dayOfWeek] = { total: 0, sum: 0 };
          }
          const dayData = byDayOfWeek[s.dayOfWeek];
          if (dayData) {
            dayData.total++;
            dayData.sum += s.completionRate;
          }
        });

        const dayAverages = Object.entries(byDayOfWeek).map(([day, data]) => ({
          day: parseInt(day),
          avg: data.sum / data.total,
        }));

        const sortedDays = dayAverages.sort((a, b) => b.avg - a.avg);
        const bestDayOfWeek = sortedDays[0]?.day ?? 1;
        const worstDayOfWeek = sortedDays[sortedDays.length - 1]?.day ?? 0;

        // Obtener completados del período para análisis de protocolos
        const completions = await db.completions
          .where('date')
          .between(startDate, endDate, true, true)
          .toArray();

        // Calcular mejor bloque horario
        const byTimeBlock: Record<string, number> = { morning: 0, afternoon: 0, evening: 0 };
        completions.forEach((c) => {
          if (c.timeBlock) {
            const current = byTimeBlock[c.timeBlock];
            if (current !== undefined) {
              byTimeBlock[c.timeBlock] = current + 1;
            }
          }
        });

        const bestTimeBlock = (Object.entries(byTimeBlock).sort((a, b) => b[1] - a[1])[0]?.[0] ??
          'morning') as 'morning' | 'afternoon' | 'evening';

        // Calcular consistencia por protocolo
        const protocolStats = await calculateProtocolConsistency(completions, startDate, endDate);

        const totalTimeSpent = snapshots.reduce((acc, s) => acc + s.completedTimeEstimate, 0);

        const stats: PeriodStats = {
          period,
          startDate,
          endDate,
          avgCompletionRate: Math.round(avgCompletionRate),
          avgByLayer: {
            survival: Math.round(avgByLayer.survival),
            maintenance: Math.round(avgByLayer.maintenance),
            growth: Math.round(avgByLayer.growth),
          },
          activeDays,
          totalDays,
          currentStreak,
          bestStreak,
          bestDayOfWeek,
          worstDayOfWeek,
          bestTimeBlock,
          mostConsistent: protocolStats.slice(0, 5),
          leastConsistent: protocolStats.slice(-5).reverse(),
          totalTimeSpent,
          avgDailyTime: activeDays > 0 ? Math.round(totalTimeSpent / activeDays) : 0,
        };

        set({ isLoading: false });
        return stats;
      } catch (error) {
        console.error('Error calculating stats:', error);
        set({ isLoading: false, error: 'Error calculando estadísticas' });
        return null;
      }
    },

    getProtocolConsistency: async (protocolId) => {
      try {
        const { startDate, endDate } = getPeriodDates('month');

        const completions = await db.completions
          .where('protocolId')
          .equals(protocolId)
          .filter((c) => c.date >= startDate && c.date <= endDate)
          .toArray();

        if (completions.length === 0) return null;

        const first = completions[0];
        if (!first) return null;

        const totalDays = getDaysBetween(startDate, endDate);

        // Calcular racha especifica del protocolo
        const currentStreak = calculateProtocolStreak(completions);

        return {
          protocolId,
          protocolTitle: first.protocolTitle,
          layer: first.layer,
          frequency: first.frequency,
          timesAvailable: totalDays,
          timesCompleted: completions.length,
          completionRate: Math.round((completions.length / totalDays) * 100),
          currentStreak,
        };
      } catch (error) {
        console.error('Error getting protocol consistency:', error);
        return null;
      }
    },

    // === Actions: Queries ===
    getCompletions: async (filters) => {
      try {
        let query = db.completions.toCollection();

        if (filters.startDate && filters.endDate) {
          query = db.completions
            .where('date')
            .between(filters.startDate, filters.endDate, true, true);
        } else if (filters.startDate) {
          query = db.completions.where('date').aboveOrEqual(filters.startDate);
        }

        let results = await query.toArray();

        // Aplicar filtros adicionales
        if (filters.frequency) {
          results = results.filter((c) => c.frequency === filters.frequency);
        }
        if (filters.layer) {
          results = results.filter((c) => c.layer === filters.layer);
        }
        if (filters.protocolId) {
          results = results.filter((c) => c.protocolId === filters.protocolId);
        }

        return results;
      } catch (error) {
        console.error('Error getting completions:', error);
        return [];
      }
    },

    getDaySnapshots: async (startDate, endDate) => {
      try {
        return await db.daySnapshots
          .where('date')
          .between(startDate, endDate, true, true)
          .toArray();
      } catch (error) {
        console.error('Error getting snapshots:', error);
        return [];
      }
    },

    getStreak: async () => {
      try {
        const snapshots = await db.daySnapshots.orderBy('date').reverse().limit(365).toArray();

        if (snapshots.length === 0) return 0;

        let streak = 0;
        const today = getTodayDate();
        let expectedDate = today;

        for (const snapshot of snapshots) {
          if (snapshot.date === expectedDate && snapshot.completionRate >= 50) {
            streak++;
            // Calcular el día anterior
            const d = new Date(expectedDate);
            d.setDate(d.getDate() - 1);
            expectedDate = d.toISOString().split('T')[0] ?? expectedDate;
          } else if (snapshot.date < expectedDate) {
            // Día sin registro, rompe la racha
            break;
          }
        }

        return streak;
      } catch (error) {
        console.error('Error getting streak:', error);
        return 0;
      }
    },

    // === Actions: Lifecycle ===
    initialize: async () => {
      try {
        set({ isLoading: true });
        await db.open();
        await get().loadTodaySnapshot();
        await get().calculateWeekStats();
        set({ isLoading: false });
      } catch (error) {
        console.error('Error initializing history:', error);
        set({ isLoading: false, error: 'Error inicializando historial' });
      }
    },
  }))
);

// === Helpers ===

function getTimeBlockFromHour(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

function getPeriodDates(period: 'week' | 'month' | 'quarter' | 'year' | 'all'): {
  startDate: string;
  endDate: string;
} {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0] ?? getTodayDate();
  let startDate: string;

  switch (period) {
    case 'week': {
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay() + 1); // Lunes
      startDate = weekStart.toISOString().split('T')[0] ?? endDate;
      break;
    }

    case 'month':
      startDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      break;

    case 'quarter': {
      const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
      startDate = `${now.getFullYear()}-${String(quarterMonth + 1).padStart(2, '0')}-01`;
      break;
    }

    case 'year':
      startDate = `${now.getFullYear()}-01-01`;
      break;

    case 'all':
    default:
      startDate = '2020-01-01';
      break;
  }

  return { startDate, endDate };
}

function getDaysBetween(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}

function calculateStreaks(snapshots: DaySnapshot[]): { currentStreak: number; bestStreak: number } {
  if (snapshots.length === 0) return { currentStreak: 0, bestStreak: 0 };

  // Ordenar por fecha descendente
  const sorted = [...snapshots].sort((a, b) => b.date.localeCompare(a.date));

  let currentStreak = 0;
  let bestStreak = 0;
  let tempStreak = 0;
  let expectedDate = getTodayDate();

  for (const snapshot of sorted) {
    if (snapshot.date === expectedDate && snapshot.completionRate >= 50) {
      tempStreak++;
      if (currentStreak === 0) currentStreak = tempStreak;
      bestStreak = Math.max(bestStreak, tempStreak);

      const d = new Date(expectedDate);
      d.setDate(d.getDate() - 1);
      expectedDate = d.toISOString().split('T')[0] ?? expectedDate;
    } else if (snapshot.date < expectedDate) {
      tempStreak = 0;
      expectedDate = snapshot.date;

      const d = new Date(expectedDate);
      d.setDate(d.getDate() - 1);
      expectedDate = d.toISOString().split('T')[0] ?? expectedDate;
    }
  }

  return { currentStreak, bestStreak };
}

/**
 * Calcula la racha actual de un protocolo especifico
 * basandose en sus completados
 */
function calculateProtocolStreak(completions: CompletionRecord[]): number {
  if (completions.length === 0) return 0;

  // Obtener fechas unicas ordenadas descendentemente
  const uniqueDates = [...new Set(completions.map((c) => c.date))].sort((a, b) => b.localeCompare(a));

  if (uniqueDates.length === 0) return 0;

  let streak = 0;
  const today = getTodayDate();
  let expectedDate = today;

  for (const date of uniqueDates) {
    if (date === expectedDate) {
      streak++;
      // Calcular el dia anterior
      const d = new Date(expectedDate);
      d.setDate(d.getDate() - 1);
      expectedDate = d.toISOString().split('T')[0] ?? expectedDate;
    } else if (date < expectedDate) {
      // Si la fecha es anterior a la esperada, rompe la racha
      // (hay un hueco entre fechas)
      break;
    }
  }

  return streak;
}

async function calculateProtocolConsistency(
  completions: CompletionRecord[],
  _startDate: string,
  _endDate: string
): Promise<ProtocolConsistency[]> {
  // Agrupar por protocolo
  const byProtocol: Record<
    string,
    {
      protocolId: string;
      protocolTitle: string;
      layer: Layer;
      frequency: Frequency;
      completions: CompletionRecord[];
    }
  > = {};

  completions.forEach((c) => {
    if (!byProtocol[c.protocolId]) {
      byProtocol[c.protocolId] = {
        protocolId: c.protocolId,
        protocolTitle: c.protocolTitle,
        layer: c.layer,
        frequency: c.frequency,
        completions: [],
      };
    }
    const protocolData = byProtocol[c.protocolId];
    if (protocolData) {
      protocolData.completions.push(c);
    }
  });

  // Calcular consistencia
  return Object.values(byProtocol)
    .filter((p) => p.frequency === 'daily') // Solo diarios para consistencia
    .map((p) => {
      const uniqueDays = new Set(p.completions.map((c) => c.date)).size;
      const totalDays = getDaysBetween(_startDate, _endDate);

      // Calcular racha especifica del protocolo
      const currentStreak = calculateProtocolStreak(p.completions);

      return {
        protocolId: p.protocolId,
        protocolTitle: p.protocolTitle,
        layer: p.layer,
        frequency: p.frequency,
        timesAvailable: totalDays,
        timesCompleted: uniqueDays,
        completionRate: Math.round((uniqueDays / totalDays) * 100),
        currentStreak,
      };
    })
    .sort((a, b) => b.completionRate - a.completionRate);
}
